import { useState } from 'react'
import { Info } from 'lucide-react'
import { ErrorState, FilterBar, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useComplianceMutations, useNationalisation } from '../api'

/**
 * The nationalisation ratio.
 *
 * <p>Two different absences are shown as two different sentences, because they mean different
 * things: no target set is a question nobody has answered, and no nationalities counted is a
 * ratio that cannot be computed. Neither is ever rendered as 0%, which would read as a company
 * that has hired nobody local.
 */
export function NationalisationPage() {
  useBreadcrumb()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const [chosenEntity, setChosenEntity] = useState('')
  const [year, setYear] = useState(new Date().getFullYear())

  // Derived rather than stored-then-corrected: until somebody picks one, the entity is the
  // first the server returned. A default written into state by an effect paints the empty
  // screen first and then the real one.
  const entityId = chosenEntity || entities.data?.[0]?.id || ''

  const status = useNationalisation(entityId || undefined, year)
  const actions = useComplianceMutations()

  const [target, setTarget] = useState('')
  const [basis, setBasis] = useState('HEADCOUNT')
  const [nationalities, setNationalities] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loadedFor, setLoadedFor] = useState('')

  // The form is seeded from the server once per entity-and-year and then belongs to whoever
  // is typing in it. Adjusted during render rather than in an effect: an effect would paint
  // the previous entity's figures for a frame before correcting them.
  const key = `${entityId}:${year}`
  if (status.data && loadedFor !== key) {
    setTarget(status.data.targetPercent?.toString() ?? '')
    setBasis(status.data.basis)
    setNationalities(status.data.countedNationalities.join(', '))
    setLoadedFor(key)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Nationalisation"
        subtitle="Where an entity stands, and against what."
      />

      <FilterBar>
        <Select
          aria-label="Legal entity"
          value={entityId}
          onChange={(event) => { setChosenEntity(event.target.value); setLoadedFor('') }}
        >
          {entities.data?.map((entity) => (
            <option key={entity.id} value={entity.id}>{entity.name}</option>
          ))}
        </Select>
        <Input
          aria-label="Year"
          type="number"
          className="w-24"
          value={String(year)}
          onChange={(event) => { setYear(Number(event.target.value)); setLoadedFor('') }}
        />
      </FilterBar>

      {status.isError && (
        <ErrorState error={status.error} onRetry={() => void status.refetch()} />
      )}
      {status.isPending && entityId && <Skeleton className="h-32" />}

      {status.data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">
              Where it stands
            </h3>
            {status.data.note && (
              <p className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {status.data.note}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-[var(--color-muted)]">On headcount</dt>
                <dd className="text-lg font-semibold text-[var(--color-heading)]">
                  {status.data.headcount}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Counting towards it</dt>
                <dd className="text-lg font-semibold text-[var(--color-heading)]">
                  {status.data.qualifying ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Actual</dt>
                <dd className="text-lg font-semibold text-[var(--color-heading)]">
                  {status.data.actualPercent == null
                    ? <span className="text-sm font-normal text-[var(--color-muted)]">Not computed</span>
                    : `${status.data.actualPercent}%`}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Target</dt>
                <dd className="text-lg font-semibold text-[var(--color-heading)]">
                  {status.data.targetPercent == null
                    ? <span className="text-sm font-normal text-[var(--color-muted)]">Not set</span>
                    : `${status.data.targetPercent}%`}
                </dd>
              </div>
              {status.data.gap != null && (
                <div className="col-span-2">
                  <dt className="text-xs text-[var(--color-muted)]">Gap</dt>
                  <dd className={`text-lg font-semibold ${Number(status.data.gap) > 0
                    ? 'text-[var(--color-danger)]' : 'text-[var(--color-success)]'}`}>
                    {Number(status.data.gap) > 0
                      ? `${status.data.gap} points short`
                      : `${Math.abs(Number(status.data.gap))} points clear`}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">
              What it is measured against
            </h3>
            {error && (
              <p role="alert" className="mb-3 rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-2 text-sm text-[var(--color-danger)]">
                {error}
              </p>
            )}
            <div className="space-y-3">
              <div>
                <Label htmlFor="nat-target">Target percentage</Label>
                <Input
                  id="nat-target" type="number" step="0.01" value={target}
                  onChange={(event) => setTarget(event.target.value)}
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Leave it empty if no target applies. There is no default: the required ratio
                  depends on the sector, the size band and the year.
                </p>
              </div>
              <div>
                <Label htmlFor="nat-basis">Basis</Label>
                <Select
                  id="nat-basis" className="w-full" value={basis}
                  onChange={(event) => setBasis(event.target.value)}
                >
                  <option value="HEADCOUNT">Headcount</option>
                  <option value="POINTS">Points</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="nat-nationalities">Nationalities that count</Label>
                <Input
                  id="nat-nationalities" value={nationalities}
                  placeholder="AE, BH, KW"
                  onChange={(event) => setNationalities(event.target.value)}
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Two-letter codes, separated by commas. With none named the ratio is not
                  computed at all — naming none is not the same as nobody qualifying.
                </p>
              </div>
              <div>
                <Label htmlFor="nat-notes">Notes</Label>
                <Textarea
                  id="nat-notes" value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
              <Button
                type="button"
                disabled={!entityId || actions.saveNationalisation.isPending}
                onClick={() => {
                  void (async () => {
                    setError(null)
                    try {
                      await actions.saveNationalisation.mutateAsync({
                        legalEntityId: entityId,
                        year,
                        targetPercent: target ? Number(target) : null,
                        basis,
                        countedNationalities: nationalities
                          .split(',').map((code) => code.trim()).filter(Boolean),
                        notes: notes || null,
                      })
                    } catch (caught) {
                      setError(toApiProblem(caught)?.detail ?? 'That could not be saved')
                    }
                  })()
                }}
              >
                Save
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
